import React, { useState } from "react";
import { Outlet } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import BottomNav from "./BottomNav";
import TenantStatusBanner from "./TenantStatusBanner";
import LegalAcceptanceBanner from "./LegalAcceptanceBanner";
import ImpersonationBanner from "@/components/auth/ImpersonationBanner";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import OnboardingReturnFab from "@/components/common/OnboardingReturnFab";
import ExecutiveDarkController from "@/components/theme/ExecutiveDarkController";
import { useAuth } from "@/context/AuthContext";
import { useImpersonation } from "@/context/ImpersonationContext";

function PlatformModeBanner() {
  // Visible top banner shown ONLY for platform_owner / platform_admin.
  // Suppressed while impersonation is active — the operator already sees
  // the dedicated ImpersonationBanner and PlatformModeBanner would be
  // misleading (we look like a tenant from the API perspective).
  const { isPlatform, user } = useAuth();
  const { isImpersonating } = useImpersonation();
  if (!isPlatform || isImpersonating) return null;
  return (
    <div
      className="bg-gradient-to-r from-[#102A43] via-[#0F2638] to-[#102A43] text-white px-4 py-1.5 text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 sticky top-0 z-40"
      data-testid="platform-mode-banner"
    >
      <ShieldCheck className="w-3.5 h-3.5 text-[#0D9488]" />
      <span>Modalità piattaforma</span>
      <span className="opacity-60 text-[10px] normal-case tracking-normal">
        ({user?.role}) — non sei un cliente; nessun abbonamento applicato
      </span>
    </div>
  );
}

export default function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-[#F7F7F5]" data-testid="app-shell">
      <ImpersonationBanner />
      <PlatformModeBanner />
      <TenantStatusBanner />
      <LegalAcceptanceBanner />
      <div className="flex flex-1 min-h-0">
        {/* Desktop sidebar */}
        <div className="hidden lg:block sticky top-0 h-screen">
          <Sidebar />
        </div>

        {/* Mobile sidebar drawer */}
        {mobileOpen && (
          <div
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer"
          >
            <div className="absolute inset-0 bg-[#0f172a]/40 backdrop-blur-sm" />
            <div
              className="absolute inset-y-0 left-0"
              onClick={(e) => e.stopPropagation()}
            >
              <Sidebar onNavigate={() => setMobileOpen(false)} />
            </div>
          </div>
        )}

        <div className="flex-1 min-w-0 flex flex-col">
          <Topbar onMenu={() => setMobileOpen((v) => !v)} />
          <main className="flex-1 min-w-0 px-3 sm:px-6 lg:px-10 py-5 sm:py-7 lg:py-8 max-w-[1400px] w-full pb-[calc(80px+env(safe-area-inset-bottom,0px))] lg:pb-8">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
            <OnboardingReturnFab />
          </main>
        </div>

        <BottomNav />
      </div>
      <ExecutiveDarkController />
    </div>
  );
}
